import styled, { css } from "styled-components";
import { borderRadius, rawColors } from "@magicbi/ui-library";
import { Check } from "lucide-react";
import { shadows } from "../../../design-system/tokens/shadows";
import { fontSizes, fontWeights } from "../../../design-system/tokens/typography";
import type { FieldTypeIconKind } from "../../registry/fieldTypeIcons";
import { Label, Row } from "../SectionToggleRow/SectionToggleRow.styles";

interface FieldRowStyleProps {
  $selected: boolean;
  $used: boolean;
  $dragging: boolean;
  $draggable: boolean;
  $toggleable: boolean;
}

/**
 * The Sections-panel depth-0 row already matches every Figma measurement of a
 * field row (6433:559552: 26px tall, 1px 4px padding, 6px radius, 6px
 * icon↔label gap, Body 3 high-emphasis label), so it is reused rather than
 * redrawn. Only box-sizing is added: `Row` is `width: 100%` + padding, and the
 * Data models panel has no box-sizing reset of its own.
 *
 * Selected = the lilac pill from the multi-select screenshot, drawn on the
 * row's existing 6px-radius box so it needs no extra padding or radius. The
 * hex is a screenshot read (Figma 6433:561051 not yet inspectable).
 */
export const FieldRow = styled(Row)<FieldRowStyleProps>`
  box-sizing: border-box;
  height: 1.875rem; // 30px, F10 field row (no spacing token)
  padding: 0 0.5rem;
  cursor: ${({ $draggable, $toggleable }) =>
    $draggable ? "grab" : $toggleable ? "pointer" : "default"};
  user-select: ${({ $toggleable }) => ($toggleable ? "none" : "auto")};

  ${({ $draggable }) =>
    $draggable &&
    css`
      &:active {
        cursor: grabbing;
      }
    `}

  ${({ $used }) =>
    $used &&
    css`
      background-color: ${rawColors.primary[100]};
    `}

  /* Being dragged: dashed outline, muted label. GAP: F10 dashed #69C6D9 has no token; nearest interactive text. */
  ${({ $dragging }) =>
    $dragging &&
    css`
      border: 1px dashed ${rawColors.text.interactive};
      color: ${rawColors.text.lowEmphasis};
      background-color: ${rawColors.structural.white};
    `}

  /* Selection outranks hover: a selected row stays lilac under the pointer. */
  ${({ $selected }) =>
    $selected &&
    css`
      &,
      &:hover {
        background-color: ${rawColors.accent.purpleLight};
      }
    `}

  /* Rows become focusable once they open a hover card or toggle; show where focus is. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadows.interactive.focus};
  }
`;

/** Check mark at the row's right edge: the open chart already uses this field. */
export const UsedMark = styled(Check)`
  flex: none;
  margin-right: 0.25rem;
  color: ${rawColors.text.interactive};
`;

/** Row name: 13/400, or 600 once the chart uses the field. */
export const FieldLabel = styled(Label)<{ $used: boolean }>`
  font-size: ${fontSizes["13"]};
  font-weight: ${({ $used }) => ($used ? fontWeights.semibold : fontWeights.regular)};
`;

/**
 * Type badge tints per kind (design FieldsPanel). Measure/Calculation/Dimension/Geographic use the
 * redesign tints; GAP: the Date tint (#FCF2DF / #8A5505) has no token, so the drill tint stands in.
 */
const BADGE_TINTS: Record<FieldTypeIconKind, { bg: string; fg: string }> = {
  number: { bg: rawColors.accent.infoBlue.bg, fg: rawColors.accent.infoBlue.solid },
  calculated: { bg: rawColors.accent.cross.bg, fg: rawColors.accent.cross.text },
  date: { bg: rawColors.accent.drill.bg, fg: rawColors.accent.drill.text },
  time: { bg: rawColors.accent.drill.bg, fg: rawColors.accent.drill.text },
  text: { bg: rawColors.primary[100], fg: rawColors.primary[500] },
  geo: { bg: rawColors.accent.positive.bg, fg: rawColors.accent.positive.text },
};

/** 18px square, radius 5, 10/700 letter. A used row turns the fill white; a dragged row greys it out. */
export const TypeBadge = styled.span<{ $kind: FieldTypeIconKind; $used: boolean; $dragging: boolean }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 1.125rem;
  height: 1.125rem;
  border-radius: ${borderRadius[5]};
  font-size: ${fontSizes["overline-4"]};
  font-weight: ${fontWeights.bold};
  background: ${({ $kind, $used, $dragging }) =>
    $dragging ? rawColors.grey[50] : $used ? rawColors.structural.white : BADGE_TINTS[$kind].bg};
  color: ${({ $kind, $dragging }) => ($dragging ? rawColors.text.subtleEmphasis : BADGE_TINTS[$kind].fg)};
`;
